// KmlSetup.js
//
// Entry screen for satellite/KML planning. Produces the planning frame that the
// shared editor runs on. A KML is OPTIONAL: the operator can start from a plain
// coordinate and draw navigation lines straight onto satellite imagery.

import React, { useState, useCallback } from 'react';
import { parseKmlFiles } from './kmlParser';
import { createFrame, createFrameFromBounds, autoResolution, estimateTileCount,
         DEFAULT_CM_PER_PIXEL, TARGET_MAX_UNITS } from './frame';
import { fromLonLat, zoneFor } from './utm';
import { chooseZoom } from './TileBackdrop';

// Canvas presets. 'margin' is metres added on EVERY side of the reference
// geometry (or the half-extent when starting from a bare coordinate).
export const CANVAS_PRESETS = [
  { id: 'tight',    label: 'Field',      margin: 75,   hint: 'the plot and little else' },
  { id: 'standard', label: 'Standard',   margin: 300,  hint: 'room for headland turns' },
  { id: 'large',    label: 'Large',      margin: 1000, hint: 'neighbouring fields' },
  { id: 'huge',     label: 'Very large', margin: 3000, hint: 'whole farm block' },
];

// Accepts "41.6814, -93.7102", "41.6814 -93.7102", or a pasted Google/Apple
// Maps URL. Deliberately no geocoding service: no API key, no third-party
// dependency, and no ToS question for a field tool.
export function parseLocation(text) {
  if (!text) return null;
  const at = text.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);          // google maps /@lat,lon
  const q = text.match(/[?&](?:q|ll|daddr)=(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/); // ?q=lat,lon
  const plain = text.trim().match(/^(-?\d+\.?\d*)\s*[, ]\s*(-?\d+\.?\d*)$/);
  const m = at || q || plain;
  if (!m) return null;
  const lat = parseFloat(m[1]);
  const lon = parseFloat(m[2]);
  if (!isFinite(lat) || !isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { lat, lon };
}

export default function KmlSetup({ onReady, onBack }) {
  const [features, setFeatures] = useState([]);
  const [bounds, setBounds] = useState(null);
  const [errors, setErrors] = useState([]);
  const [locText, setLocText] = useState('');
  const [margin, setMargin] = useState(300);
  const [autoRes, setAutoRes] = useState(true);
  const [manualCm, setManualCm] = useState(DEFAULT_CM_PER_PIXEL);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');

  const handleKml = useCallback(async (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.name.toLowerCase().endsWith('.kml'));
    if (files.length === 0) return;
    setBusy(true);
    const res = await parseKmlFiles(files);
    setFeatures(res.features);
    setErrors(res.errors);
    setBounds(res.bounds);
    // Export filenames key off this; strip the trailing _<n> the exporter adds.
    setName(files[0].name.replace(/\.kml$/i, '').replace(/_\d+$/, ''));
    setBusy(false);
  }, []);

  const loc = parseLocation(locText);
  const canStart = Boolean(bounds || loc);

  // Ground size of the reference geometry, so the canvas figures below are real
  // numbers rather than a guess.
  const geomSize = (() => {
    if (!bounds) return null;
    const z = zoneFor((bounds.minLon + bounds.maxLon) / 2, (bounds.minLat + bounds.maxLat) / 2);
    const a = fromLonLat(bounds.minLon, bounds.minLat, z);
    const b = fromLonLat(bounds.maxLon, bounds.maxLat, z);
    return { w: Math.abs(b.easting - a.easting), h: Math.abs(b.northing - a.northing) };
  })();

  const canvasW = geomSize ? Math.max(geomSize.w + 2 * margin, 200) : margin * 2;
  const canvasH = geomSize ? Math.max(geomSize.h + 2 * margin, 200) : margin * 2;
  const maxExtent = Math.max(canvasW, canvasH);
  const cmPerPixel = autoRes ? autoResolution(maxExtent) : manualCm;
  const units = Math.round((maxExtent * 100) / cmPerPixel);
  const centerLat = bounds ? (bounds.minLat + bounds.maxLat) / 2 : (loc ? loc.lat : 0);
  // The backdrop drops to a coarser tile zoom on big canvases rather than
  // truncating the grid, so report what it will actually use.
  const tz = chooseZoom(canvasW, canvasH, centerLat);
  const tiles = tz.tiles;
  const groundCmPerTilePx = (156543.03392 * Math.cos((centerLat * Math.PI) / 180) / 2 ** tz.zoom) * 100;
  const tilesOver = tz.zoom < 17;

  const start = () => {
    const frame = bounds
      ? createFrameFromBounds(bounds, { cmPerPixel, marginMeters: margin })
      : createFrame({ centerLon: loc.lon, centerLat: loc.lat, widthMeters: canvasW, heightMeters: canvasH, cmPerPixel });
    // bounds/center/margin go with it so the canvas can be resized later
    // without re-running this screen.
    onReady({
      frame, features, name: name || 'satellite_field',
      bounds, center: loc ? { lon: loc.lon, lat: loc.lat } : null, margin, autoRes, manualCm,
    });
  };

  return (
    <div style={wrap}>
      <div style={card}>
        <h2 style={{ margin: '0 0 4px 0', fontSize: 20 }}>Satellite / KML planning</h2>
        <p style={sub}>Plan on satellite imagery. A KML is optional — you can start from a coordinate and draw straight away.</p>

        <div style={sect}>
          <div style={h3}>1 · Reference geometry <span style={opt}>optional</span></div>
          <input type="file" accept=".kml" multiple onChange={handleKml} style={{ fontSize: 13 }} />
          {busy && <div style={note}>Reading…</div>}
          {features.length > 0 && (
            <div style={ok}>✓ {features.length} feature(s) loaded — the map will open on them.</div>
          )}
          {errors.map((e) => <div key={e.file} style={bad}>⚠ {e.file}: {e.message}</div>)}
          <div style={note}>Field exports are usually a folder of several KMLs — select them all.</div>
        </div>

        <div style={sect}>
          <div style={h3}>
            2 · Location {bounds ? <span style={opt}>set by the KML</span> : <span style={req}>required</span>}
          </div>
          <input
            type="text"
            value={locText}
            onChange={(e) => setLocText(e.target.value)}
            placeholder="41.6814, -93.7102  — or paste a Google Maps link"
            disabled={Boolean(bounds)}
            style={{ ...input, opacity: bounds ? 0.45 : 1 }}
          />
          {locText && !loc && !bounds && <div style={bad}>Could not read a coordinate from that.</div>}
          {loc && !bounds && <div style={ok}>✓ {loc.lat.toFixed(6)}, {loc.lon.toFixed(6)}</div>}
        </div>

        <div style={sect}>
          <div style={h3}>3 · Canvas size</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            {CANVAS_PRESETS.map((pr) => (
              <button key={pr.id} onClick={() => setMargin(pr.margin)} title={pr.hint}
                style={{ ...chip, background: margin === pr.margin ? '#0a84ff' : '#2c2c2e' }}>
                {pr.label}
              </button>
            ))}
          </div>
          <label style={lbl}>
            {bounds ? 'Margin beyond the reference geometry' : 'Half-width of the canvas'}: <b>{margin} m</b>
            <input type="range" min="50" max="5000" step="50" value={margin}
              onChange={(e) => setMargin(Number(e.target.value))} style={{ width: '100%' }} />
          </label>
          <div style={note}>
            Headland turns often fall <b>outside</b> the plot boundary, so leave room to draw them.
            You can pick any size — there is no fixed limit.
          </div>

          <div style={statBox}>
            <div><span style={statK}>Canvas</span> {Math.round(canvasW)} × {Math.round(canvasH)} m</div>
            <div><span style={statK}>Resolution</span> {cmPerPixel.toFixed(2)} cm per unit{autoRes && ' (auto)'}</div>
            <div><span style={statK}>Grid</span> {units.toLocaleString()} units across</div>
            <div><span style={statK}>Imagery</span> {groundCmPerTilePx.toFixed(0)} cm/px · {tiles.toLocaleString()} tiles</div>
          </div>

          <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={autoRes} onChange={(e) => setAutoRes(e.target.checked)} />
            <span>Auto resolution — keeps the grid near {TARGET_MAX_UNITS.toLocaleString()} units</span>
          </label>
          {!autoRes && (
            <label style={lbl}>
              Resolution: <b>{manualCm} cm</b> per unit
              <input type="range" min="1" max="50" step="0.5" value={manualCm}
                onChange={(e) => setManualCm(Number(e.target.value))} style={{ width: '100%' }} />
            </label>
          )}
          {!autoRes && units > TARGET_MAX_UNITS * 1.5 && (
            <div style={bad}>
              ⚠ {units.toLocaleString()} units is well past what the editor's on-screen sizes are
              tuned for — handles and line widths will look small. Re-enable auto resolution.
            </div>
          )}
          {tilesOver && (
            <div style={note}>
              A canvas this large drops to coarser satellite imagery
              (~{groundCmPerTilePx.toFixed(0)} cm/px) to keep the tile count sane. Zoom in after
              opening for detail near the field, or use a smaller canvas.
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          {onBack && <button onClick={onBack} style={{ ...btn, background: '#3a3a3c' }}>← Back</button>}
          <button onClick={start} disabled={!canStart}
            style={{ ...btn, flex: 1, background: canStart ? '#2e7d32' : '#3a3a3c', cursor: canStart ? 'pointer' : 'not-allowed' }}>
            Open planning canvas
          </button>
        </div>
      </div>
    </div>
  );
}

const wrap = { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#111', overflowY: 'auto' };
const card = { width: 520, maxWidth: '92vw', background: '#1c1c1e', color: '#f2f2f7', borderRadius: 12, padding: 24, fontFamily: 'system-ui, Arial, sans-serif', fontSize: 13, boxShadow: '0 20px 60px rgba(0,0,0,.5)' };
const sub = { margin: '0 0 4px 0', opacity: 0.65, lineHeight: 1.45 };
const sect = { marginTop: 20, paddingTop: 14, borderTop: '1px solid #333' };
const h3 = { fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.7, opacity: 0.6, marginBottom: 8 };
const opt = { textTransform: 'none', letterSpacing: 0, opacity: 0.7, fontStyle: 'italic' };
const req = { textTransform: 'none', letterSpacing: 0, color: '#ff9f0a' };
const input = { width: '100%', boxSizing: 'border-box', background: '#2c2c2e', color: '#f2f2f7', border: '1px solid #48484a', borderRadius: 6, padding: 9, fontSize: 13 };
const lbl = { display: 'block', marginTop: 10, opacity: 0.85 };
const note = { marginTop: 6, fontSize: 11, opacity: 0.55, lineHeight: 1.45 };
const ok = { marginTop: 6, color: '#30d158' };
const bad = { marginTop: 6, color: '#ff6b6b' };
const chip = { border: '1px solid #48484a', borderRadius: 999, padding: '5px 12px', color: '#f2f2f7', fontSize: 12, cursor: 'pointer' };
const statBox = { marginTop: 12, background: '#2c2c2e', borderRadius: 8, padding: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 14px', fontSize: 12 };
const statK = { opacity: 0.55, marginRight: 6 };
const btn = { border: 'none', borderRadius: 8, padding: '11px 16px', color: 'white', fontSize: 14, cursor: 'pointer' };
